<script setup>
import { ref, watch, computed } from 'vue';
import AiDraftsApi from 'dashboard/api/inbox/aiDrafts';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [Number, String], default: null },
  aiDraftId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close']);
const { t } = useI18n();

const data = ref(null);
const loading = ref(false);
const error = ref(null);
const showRaw = ref(false);

const close = () => {
  if (loading.value) return;
  emit('close');
};

const fetchData = async () => {
  if (!props.conversationId || !props.aiDraftId) {
    error.value = t('CLASSIFIER_MODAL.ERROR_NO_DRAFT');
    return;
  }
  loading.value = true;
  error.value = null;
  data.value = null;
  try {
    const resp = await AiDraftsApi.classifierAnalysis(
      props.conversationId,
      props.aiDraftId
    );
    data.value = resp?.data?.data || null;
  } catch (e) {
    if (e?.response?.status === 404) {
      error.value = t('CLASSIFIER_MODAL.ERROR_NOT_AVAILABLE');
    } else {
      error.value = e?.message || String(e);
    }
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.show,
  v => {
    if (v) fetchData();
    else {
      // reset on close so next open re-fetches fresh
      data.value = null;
      error.value = null;
      showRaw.value = false;
    }
  }
);

// --- formatters --------------------------------------------------------
const confidencePct = computed(() => {
  const c = data.value?.confidence;
  if (typeof c !== 'number') return null;
  return `${Math.round(c * 100)}%`;
});

const confidenceColor = computed(() => {
  const c = data.value?.confidence;
  if (typeof c !== 'number') return 'text-n-slate-11';
  if (c >= 0.8) return 'text-emerald-600';
  if (c >= 0.5) return 'text-amber-600';
  return 'text-red-600';
});

const riskColor = risk => {
  if (risk === 'high') return 'bg-red-50 border-red-200 text-red-700';
  if (risk === 'medium') return 'bg-amber-50 border-amber-200 text-amber-700';
  if (risk === 'low') return 'bg-yellow-50 border-yellow-200 text-yellow-700';
  return 'bg-emerald-50 border-emerald-200 text-emerald-700';
};

const infoCollectedEntries = computed(() => {
  const obj = data.value?.info_collected;
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj);
});

const infoMissing = computed(() => {
  const arr = data.value?.info_missing;
  return Array.isArray(arr) ? arr : [];
});

const escalation = computed(() => data.value?.escalation_signals || {});
const observedCues = computed(() => {
  const cues = escalation.value.observed_cues;
  return Array.isArray(cues) ? cues : [];
});
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="close"
  >
    <div
      class="bg-n-background dark:bg-n-slate-2 w-full max-w-2xl rounded-lg shadow-xl flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <header
        class="flex items-start justify-between px-5 py-4 border-b border-n-slate-4"
      >
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('CLASSIFIER_MODAL.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ $t('CLASSIFIER_MODAL.SUBTITLE') }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 p-1 -mr-1 -mt-1"
          @click="close"
        >
          <span class="i-ph-x text-lg" />
        </button>
      </header>

      <!-- Body -->
      <div class="px-5 py-4 overflow-y-auto space-y-4 text-sm">
        <!-- Loading -->
        <div v-if="loading" class="text-center text-n-slate-11 py-10">
          <span class="i-lucide-loader-2 animate-spin inline-block text-2xl" />
          <p class="mt-2 text-xs">{{ $t('CLASSIFIER_MODAL.LOADING') }}</p>
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
        >
          {{ error }}
        </div>

        <!-- Content -->
        <template v-else-if="data">
          <!-- Top: location / stage / handler / confidence -->
          <div class="grid grid-cols-2 gap-3">
            <div class="border border-n-slate-4 rounded p-2">
              <div class="text-[11px] uppercase tracking-wide text-n-slate-11">
                {{ $t('CLASSIFIER_MODAL.FIELDS.LOCATION') }}
              </div>
              <div class="font-mono text-xs mt-1 break-all text-n-slate-12">
                {{ data.location || '—' }}
              </div>
            </div>
            <div class="border border-n-slate-4 rounded p-2">
              <div class="text-[11px] uppercase tracking-wide text-n-slate-11">
                {{ $t('CLASSIFIER_MODAL.FIELDS.STAGE') }}
              </div>
              <div class="font-mono text-xs mt-1 break-all text-n-slate-12">
                {{ data.stage || '—' }}
              </div>
            </div>
            <div class="border border-n-slate-4 rounded p-2">
              <div class="text-[11px] uppercase tracking-wide text-n-slate-11">
                {{ $t('CLASSIFIER_MODAL.FIELDS.HANDLER') }}
              </div>
              <div class="font-mono text-xs mt-1 break-all text-n-slate-12">
                {{ data.handler || '—' }}
              </div>
            </div>
            <div class="border border-n-slate-4 rounded p-2">
              <div class="text-[11px] uppercase tracking-wide text-n-slate-11">
                {{ $t('CLASSIFIER_MODAL.FIELDS.CONFIDENCE') }}
              </div>
              <div class="font-mono text-xs mt-1" :class="confidenceColor">
                {{ confidencePct || '—' }}
              </div>
            </div>
          </div>

          <!-- Reasoning (the key Chinese explanation) -->
          <div v-if="data.reasoning">
            <div
              class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1"
            >
              {{ $t('CLASSIFIER_MODAL.FIELDS.REASONING') }}
            </div>
            <div
              class="text-sm whitespace-pre-wrap leading-relaxed bg-n-slate-2 dark:bg-n-slate-3 rounded p-3 border border-n-slate-4 text-n-slate-12"
            >
              {{ data.reasoning }}
            </div>
          </div>

          <!-- Escalation signals -->
          <div>
            <div
              class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1"
            >
              {{ $t('CLASSIFIER_MODAL.FIELDS.ESCALATION') }}
            </div>
            <div class="space-y-1.5 text-xs">
              <div class="flex items-center gap-2">
                <span class="text-n-slate-11">
                  {{ $t('CLASSIFIER_MODAL.FIELDS.COMPLAINT_RISK') }}:
                </span>
                <span
                  class="inline-block px-1.5 py-0.5 border rounded font-mono"
                  :class="riskColor(escalation.complaint_risk)"
                >
                  {{ escalation.complaint_risk || 'none' }}
                </span>
              </div>
              <div v-if="observedCues.length > 0">
                <span class="text-n-slate-11">
                  {{ $t('CLASSIFIER_MODAL.FIELDS.OBSERVED_CUES') }}:
                </span>
                <ul
                  class="list-disc list-inside ml-2 mt-1 space-y-0.5 text-n-slate-12"
                >
                  <li v-for="(c, i) in observedCues" :key="i">{{ c }}</li>
                </ul>
              </div>
              <div v-if="escalation.recommended_ladder_start">
                <span class="text-n-slate-11">
                  {{ $t('CLASSIFIER_MODAL.FIELDS.LADDER') }}:
                </span>
                <span class="font-mono ml-1 text-n-slate-12">{{
                  escalation.recommended_ladder_start
                }}</span>
              </div>
            </div>
          </div>

          <!-- Info collected / missing -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div
                class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('CLASSIFIER_MODAL.FIELDS.INFO_COLLECTED') }}
              </div>
              <div
                v-if="infoCollectedEntries.length === 0"
                class="text-xs text-n-slate-11 italic"
              >
                {{ $t('CLASSIFIER_MODAL.NONE') }}
              </div>
              <ul v-else class="text-xs space-y-0.5">
                <li v-for="[k, v] in infoCollectedEntries" :key="k">
                  <span class="font-mono text-n-slate-11">{{ k }}:</span>
                  <span class="ml-1 text-n-slate-12">{{ v }}</span>
                </li>
              </ul>
            </div>
            <div>
              <div
                class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1"
              >
                {{ $t('CLASSIFIER_MODAL.FIELDS.INFO_MISSING') }}
              </div>
              <div
                v-if="infoMissing.length === 0"
                class="text-xs text-n-slate-11 italic"
              >
                {{ $t('CLASSIFIER_MODAL.NONE') }}
              </div>
              <ul v-else class="text-xs space-y-0.5">
                <li
                  v-for="(k, i) in infoMissing"
                  :key="i"
                  class="font-mono text-amber-700"
                >
                  {{ k }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Optional resolved branch -->
          <div v-if="data.resolved_branch">
            <div
              class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1"
            >
              {{ $t('CLASSIFIER_MODAL.FIELDS.RESOLVED_BRANCH') }}
            </div>
            <div class="font-mono text-xs text-n-slate-12 break-all">
              {{ data.resolved_branch }}
            </div>
          </div>

          <!-- Raw payload (folded) -->
          <details class="text-xs border-t border-n-slate-4 pt-3">
            <summary class="cursor-pointer text-n-slate-11 select-none">
              {{ $t('CLASSIFIER_MODAL.RAW_PAYLOAD') }}
            </summary>
            <pre
              class="mt-2 bg-n-slate-2 dark:bg-n-slate-3 rounded p-2 overflow-x-auto text-[11px] whitespace-pre text-n-slate-12"
              >{{ JSON.stringify(data, null, 2) }}
            </pre>
          </details>
        </template>
      </div>

      <!-- Footer -->
      <footer
        class="flex justify-end gap-2 px-5 py-3 border-t border-n-slate-4 bg-n-slate-1/50"
      >
        <button
          type="button"
          class="text-sm px-3 py-1.5 rounded-md text-n-slate-11 hover:bg-n-slate-3"
          @click="close"
        >
          {{ $t('CLASSIFIER_MODAL.CLOSE') }}
        </button>
      </footer>
    </div>
  </div>
</template>
